import { MutationFunction, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import {
  Module,
  PATH_PROJECT_SETTLEMENTS_DETAILS,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS,
} from "@/constants";
import { ApiError } from "@/services/api";
import { invoiceSpecificationsService } from "@/services/invoiceSpecifications";
import { useInvoiceSpecificationId } from "@/modules/projects/hooks/useInvoiceSpecificationId";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useSettlementId } from "@/modules/projects/hooks/useSettlementId";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

export const useDeleteInvoiceSpecification = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invoiceSpecificationId = useInvoiceSpecificationId();
  const settlementId = useSettlementId();
  const routeProjectId = useProjectId();

  const mutationFn: MutationFunction<undefined, undefined> = async () => {
    await invoiceSpecificationsService.delete(invoiceSpecificationId);
    return undefined;
  };

  return useMutationWithErrorHandling<
    undefined,
    ApiError,
    undefined,
    undefined
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Module.ProjectSettlements, settlementId],
      });
      toast({ title: t("projectInvoiceSpecifications.success.delete") });
      navigate(
        routeProjectId
          ? generatePath(PATH_PROJECT_SETTLEMENTS_DETAILS, {
              projectId: routeProjectId,
              settlementId,
            })
          : generatePath(PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS, {
              settlementId,
            }),
      );
    },
    errorTitle: t("projectInvoiceSpecifications.error.delete"),
  });
};
